import { Button, Text, toast } from "@medusajs/ui"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useRef, useState } from "react"
import { errorMessage, sdk } from "@/lib/sdk"

type Issue = { line: number; handle?: string; message: string }
type Result = { dry_run: boolean; products: number; variants: number; created: number; skipped: number; errors: Issue[]; warnings: Issue[] }
const MAX_BYTES = 2_500_000

/** Products → Import: pick a CSV, review the validation report, then apply. Nothing is written until "Import". */
export function ProductImportDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const input = useRef<HTMLInputElement>(null)
  const [csv, setCsv] = useState<string | null>(null)
  const [name, setName] = useState("")
  const [report, setReport] = useState<Result | null>(null)
  const run = useMutation({
    mutationFn: (v: { csv: string; dry_run: boolean }) => sdk.client.fetch<Result>("/admin/terify/products/import", { method: "POST", body: v }),
    onSuccess: (r) => {
      setReport(r)
      if (!r.dry_run) {
        qc.invalidateQueries({ queryKey: ["products"] })
        if (!r.errors.length) toast.success(`${r.created} product${r.created === 1 ? "" : "s"} imported`)
      }
    },
    onError: (e) => toast.error(errorMessage(e)),
  })
  const pick = async (file: File | undefined) => {
    setReport(null)
    if (!file) return
    if (file.size > MAX_BYTES) return toast.error("That file is too large (2.5 MB max). Split it into smaller files.")
    const text = await file.text()
    setCsv(text)
    setName(file.name)
    run.mutate({ csv: text, dry_run: true })
  }
  const ready = !!report && report.dry_run && !report.errors.length && report.products - report.skipped > 0
  const done = !!report && !report.dry_run && !report.errors.length
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" role="dialog" aria-modal="true" aria-label="Import products">
      <div className="my-8 w-full max-w-xl rounded-xl bg-ui-bg-base shadow-xl">
        <div className="border-b border-ui-border-base px-5 py-4 text-[15px] font-semibold">Import products by CSV</div>
        <div className="flex flex-col gap-3 p-5">
          <Text size="small" className="text-ui-fg-subtle">
            Use the same columns as the product export: Handle, Title, Status, Description, Type, Collection, Tags, Variant, Variant SKU, Variant barcode, Variant price, Compare-at price, Inventory quantity. Only title and price are required. Existing handles are skipped; any error stops the whole import.
          </Text>
          <div className="flex items-center gap-3">
            <input ref={input} type="file" accept=".csv,text/csv" className="sr-only" aria-label="CSV file" onChange={(e) => pick(e.target.files?.[0])} />
            <Button size="small" variant="secondary" onClick={() => input.current?.click()}>{csv ? "Choose another file" : "Choose CSV file"}</Button>
            <Text size="small" className="truncate text-ui-fg-subtle">{name}</Text>
          </div>
          {run.isPending && <Text size="small">{report ? "Importing…" : "Checking file…"}</Text>}
          {report && (
            <div className="rounded-lg bg-ui-bg-subtle p-3 text-[13px]" aria-live="polite">
              <div className="font-semibold">
                {report.dry_run ? `Found ${report.products} product${report.products === 1 ? "" : "s"} with ${report.variants} variant${report.variants === 1 ? "" : "s"}` : `${report.created} product${report.created === 1 ? "" : "s"} created`}
                {report.skipped > 0 && ` · ${report.skipped} skipped`}
              </div>
              {report.errors.length > 0 && (
                <ul className="mt-2 list-disc space-y-0.5 pl-5 text-ui-fg-error">
                  {report.errors.slice(0, 25).map((e, i) => <li key={i}>{e.line ? `Line ${e.line}: ` : ""}{e.message}</li>)}
                  {report.errors.length > 25 && <li>…and {report.errors.length - 25} more</li>}
                </ul>
              )}
              {report.warnings.length > 0 && (
                <ul className="mt-2 list-disc space-y-0.5 pl-5 text-ui-fg-subtle">
                  {report.warnings.slice(0, 10).map((e, i) => <li key={i}>{e.handle ? `${e.handle}: ` : ""}{e.message}</li>)}
                  {report.warnings.length > 10 && <li>…and {report.warnings.length - 10} more</li>}
                </ul>
              )}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-ui-border-base px-5 py-3">
          <Button variant="secondary" size="small" onClick={onClose}>{done ? "Close" : "Cancel"}</Button>
          {!done && <Button size="small" disabled={!ready} isLoading={run.isPending && !!report && !report.dry_run} onClick={() => csv && run.mutate({ csv, dry_run: false })}>Import</Button>}
        </div>
      </div>
    </div>
  )
}
